import { useContext, useMemo, useState } from 'react';

import {
  SlideViewerContextProvider,
  StyledSlideViewer,
  usePrismTheme,
} from '@ftrprf/tailwind-components';

import useTeacherCreateContentFeedback from 'hooks/api/plannerService/teacher/mutations/useTeacherCreateContentFeedback';
import useTitle from 'hooks/useTitle';

import { UserContext } from 'providers/UserProvider';
import contentTypes from 'utils/constants/contentTypes';
import { generateScratchExercisePath } from './utils/helpers';
import { FakeQuickPlanDialog } from './partials/FakeQuickPlanDialog';

export function SlideViewer({
  generateCurrentLessonPath,
  generateLessonPath,
  isCodefever = false,
  isLoadingLesson,
  isLoadingSlides,
  isSubmittingAnswers,
  lesson,
  lessonSessionId,
  onChangeSlide,
  onChangeViewMode,
  onClose,
  onSubmitQuestionAnswers,
  // nowhere used
  refetchAnswers,
  selectedPeriodIsNotActivePeriod,
  setViewMode,
  slideId,
  slides: rawSlides,
  studioId,
  submittedQuestionAnswers,
  userGroupInfo,
  viewMode,
}) {
  const user = useContext(UserContext);
  const { organization, settings } = user;
  const [isOpenQuickPlanModal, setIsOpenQuickPlanModal] = useState(false);
  usePrismTheme(settings?.slideViewer?.codeBlocksTheme);

  useTitle(
    lesson?.title ||
      rawSlides?.filter((slide) => slide.id === slideId)[0]?.title,
  );
  const { isStudent } = user;

  const slides = useMemo(
    () =>
      rawSlides?.map((slide) => {
        // transform questions to question
        // we agreed to support multiple questions on one slide in the future,
        // but for now we'll only allow one question.
        if (!slide.questions) {
          return slide;
        }

        return {
          ...slide,
          question: slide.questions?.[0],
        };
      }),
    [rawSlides],
  );

  const slideIndex = useMemo(
    () =>
      slides
        ? slides.find((item, index) => {
            if (item.id === slideId) return index;
            return 0;
          })
        : 0,
    [slideId, slides],
  );

  const { isLoading: saveLoading, mutate: createContentFeedback } =
    useTeacherCreateContentFeedback();

  const isCk5 = lesson ? lesson.editorVersion === 5 : true;

  return (
    <SlideViewerContextProvider
      canOverrideAnswers={contentTypes[lesson?.type] === contentTypes.EXAM}
      generateCurrentLessonPath={generateCurrentLessonPath}
      generateLessonPath={generateLessonPath}
      generateScratchExercisePath={({ versionId }) =>
        generateScratchExercisePath({
          studioId,
          versionId,
        })
      }
      hasMultiScreenMode={!isStudent}
      isCk5={isCk5}
      isCodefever={isCodefever}
      isDemo
      isSubmittingAnswers={isSubmittingAnswers}
      lesson={lesson}
      lessonSessionId={lessonSessionId}
      onChangeSlide={onChangeSlide}
      onChangeViewMode={onChangeViewMode}
      onSubmitQuestionAnswers={onSubmitQuestionAnswers}
      quickplan
      refetchAnswers={refetchAnswers}
      selectedPeriodIsNotActivePeriod={selectedPeriodIsNotActivePeriod}
      setViewMode={setViewMode}
      slideId={slideId}
      slideIndex={slideIndex}
      slides={slides}
      studioId={studioId}
      submittedQuestionAnswers={submittedQuestionAnswers}
      toggleQuickplan={() => setIsOpenQuickPlanModal(!isOpenQuickPlanModal)}
      user={user}
      userDoesNotHaveSteams={organization?.steams === false}
      userGroupInfo={userGroupInfo}
      useTeacherCreateContentFeedback={{
        createContentFeedback,
        saveLoading,
      }}
      viewMode={viewMode}
    >
      <StyledSlideViewer
        isLoading={isLoadingSlides || isLoadingLesson}
        onClose={onClose}
        showTeacherInfoTab={!isStudent}
        showViewModes={!isStudent}
      />
      <FakeQuickPlanDialog
        isOpen={isOpenQuickPlanModal}
        onClose={() => {
          setIsOpenQuickPlanModal(false);
        }}
      />
    </SlideViewerContextProvider>
  );
}
